import React, { useState, useCallback, useMemo } from 'react';
import useSavingsData from '../../hooks/useSavingsData';
import { useCurrency } from '../../contexts/CurrencyContext';
import { formatSmartCurrency } from '../../utils/formatCurrency';
import moment from 'moment';
import { useTheme } from '../../contexts/ThemeContext';

const GOAL_TYPES = [
  { value: 'savings', label: 'Savings', color: '#3FD082', bg: '#ECFDF5' },
  { value: 'retirement', label: 'Retirement', color: '#9B59B6', bg: '#F5F3FF' },
  { value: 'purchase', label: 'Purchase', color: '#FF9500', bg: '#FFF7ED' },
  { value: 'emergency', label: 'Emergency', color: '#FF7283', bg: '#FFF1F2' },
  { value: 'education', label: 'Education', color: '#4882F7', bg: '#EFF6FF' },
  { value: 'other', label: 'Other', color: '#999', bg: '#F5F5F5' },
];
const GOAL_EMOJIS = ['🎯', '🏖️', '🚗', '🏠', '💰', '🎓', '💍', '🛡️', '🌱', '✈️', '👶', '💻', '🎪', '🏔️'];

const SavingsPage = () => {
  const { goals, loading, addGoal, updateGoal, deleteGoal, totalSaved, totalTarget } = useSavingsData();
  const { currencyCode } = useCurrency();
  const { theme } = useTheme();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [form, setForm] = useState({ title: '', targetAmount: '', currentAmount: '', emoji: '🎯', targetDate: '', goalType: 'savings' });

  const fmt = useCallback((cents, mode = 'long') => formatSmartCurrency(cents / 100, { mode, currencyCode }), [currencyCode]);

  const handleAdd = async () => {
    if (!form.title.trim() || !form.targetAmount) return;
    await addGoal({ title: form.title.trim(), targetAmount: Math.round(parseFloat(form.targetAmount) * 100), currentAmount: Math.round((parseFloat(form.currentAmount) || 0) * 100), emoji: form.emoji || '🎯', targetDate: form.targetDate || null, goalType: form.goalType });
    setForm({ title: '', targetAmount: '', currentAmount: '', emoji: '🎯', targetDate: '', goalType: 'savings' });
    setShowAddForm(false);
  };

  const handleSave = async (goal) => {
    const v = parseFloat(editAmount);
    if (isNaN(v)) return;
    await updateGoal(goal.id, { currentAmount: Math.round(v * 100) });
    setEditingId(null);
    setEditAmount('');
  };

  if (loading) return <div style={{ padding: 24, color: theme.tertiaryText }}>Loading...</div>;

  return (
    <div style={{ backgroundColor: theme.surfaceBackground, minHeight: '100%', padding: '0 0 100px' }}>

      {/* Horizontal goal emoji scroll */}
      {goals.length > 0 && (
        <div style={{ padding: '20px 20px 8px', overflowX: 'auto', display: 'flex', gap: 16 }}>
          {goals.map((goal) => {
            const current = goal.currentAmount || 0;
            const target = goal.targetAmount || 0;
            const monthsAway = goal.targetDate ? Math.max(0, moment(goal.targetDate).diff(moment(), 'months')) : null;
            const label = monthsAway !== null
              ? (monthsAway >= 12 ? `${Math.round(monthsAway / 12)}y` : `${monthsAway}m`)
              : '';
            return (
              <div key={goal.id} style={{ textAlign: 'center', flexShrink: 0 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 24,
                  backgroundColor: theme.badgeBg, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 24, margin: '0 auto 4px',
                  border: current >= target && target > 0 ? '2px solid #3FD082' : '2px solid transparent',
                }}>
                  {goal.emoji || '🎯'}
                </div>
                <div style={{ fontSize: 11, color: theme.tertiaryText, fontWeight: 500 }}>{label}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Heading */}
      <div style={{ padding: '16px 20px 12px', fontSize: 28, fontWeight: 800, color: theme.textColor }}>
        Goals
      </div>

      {/* Goal cards */}
      {goals.map((goal) => {
        const current = goal.currentAmount || 0;
        const target = goal.targetAmount || 0;
        const progress = target > 0 ? Math.min(100, (current / target) * 100) : 0;
        const isEditing = editingId === goal.id;
        const monthsAway = goal.targetDate ? Math.max(0, moment(goal.targetDate).diff(moment(), 'months')) : null;
        const type = GOAL_TYPES.find((t) => t.value === goal.goalType) || GOAL_TYPES[0];

        // Estimate monthly contribution
        const monthlyContribution = monthsAway && monthsAway > 0 && target > current
          ? Math.ceil((target - current) / monthsAway)
          : null;
        const paymentsLeft = monthlyContribution && monthlyContribution > 0
          ? Math.ceil((target - current) / monthlyContribution)
          : null;

        return (
          <div key={goal.id} style={{
            backgroundColor: theme.backgroundColor, borderRadius: 16, padding: '16px 20px',
            margin: '0 16px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}>
            {/* Header: emoji + name + badge + delete */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 20, backgroundColor: theme.badgeBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0,
              }}>
                {goal.emoji || '🎯'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 17, fontWeight: 700, color: theme.textColor }}>{goal.title}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, color: type.color,
                    backgroundColor: type.bg, padding: '2px 8px', borderRadius: 6,
                  }}>
                    {type.label}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: theme.tertiaryText, marginTop: 2 }}>
                  {monthsAway !== null ? `${monthsAway} months away` : 'No target date'}
                </div>
              </div>
              <button
                onClick={() => { if (window.confirm(`Delete "${goal.title}"?`)) deleteGoal(goal.id); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.disabledText, fontSize: 18, padding: 4 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#EF4444')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#D1D5DB')}
              >
                🗑
              </button>
            </div>

            {/* Large current amount */}
            <div style={{ marginTop: 12 }}>
              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSave(goal); if (e.key === 'Escape') { setEditingId(null); setEditAmount(''); } }}
                    autoFocus step="0.01"
                    style={{ width: 120, padding: '4px 8px', border: '2px solid #4882F7', borderRadius: 8, fontSize: 24, fontWeight: 700, outline: 'none' }} />
                  <button onClick={() => handleSave(goal)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3FD082', fontWeight: 700, fontSize: 18 }}>✓</button>
                  <button onClick={() => { setEditingId(null); setEditAmount(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.tertiaryText }}>✕</button>
                </div>
              ) : (
                <div
                  style={{ fontSize: 28, fontWeight: 700, color: theme.textColor, cursor: 'pointer' }}
                  onClick={() => { setEditingId(goal.id); setEditAmount((current / 100).toFixed(2)); }}
                  title="Click to update"
                >
                  {fmt(current)}
                </div>
              )}
              <div style={{ fontSize: 14, color: theme.tertiaryText, marginTop: 2 }}>of {fmt(target)}</div>
            </div>

            {/* Progress bar + percentage */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <div style={{ flex: 1, height: 3, backgroundColor: theme.badgeBg, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, backgroundColor: progress >= 100 ? '#3FD082' : '#D1D5DB', borderRadius: 2, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 13, color: theme.tertiaryText, fontWeight: 500, minWidth: 32, textAlign: 'right' }}>{Math.round(progress)}%</span>
            </div>

            {/* Contribution info */}
            {monthlyContribution && (
              <div style={{ fontSize: 13, color: theme.tertiaryText, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${theme.lightBorder}`, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>↻</span>
                <span>{fmt(monthlyContribution, 'short')} monthly</span>
                <span style={{ color: theme.disabledText }}>·</span>
                <span>{paymentsLeft} payments left</span>
              </div>
            )}
          </div>
        );
      })}

      {/* Empty state */}
      {goals.length === 0 && !showAddForm && (
        <div style={{ textAlign: 'center', padding: '60px 40px' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.textColor, marginBottom: 8 }}>No Goals Yet</div>
          <div style={{ fontSize: 15, color: theme.tertiaryText, lineHeight: '22px' }}>Set a savings target and track your progress</div>
        </div>
      )}

      {/* Add form */}
      {showAddForm ? (
        <div style={{ margin: '0 16px', backgroundColor: theme.backgroundColor, borderRadius: 16, padding: 20, border: `1px solid ${theme.borderColor}` }}>
          <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 16, color: theme.textColor }}>New Goal</div>
          <FormField label="Name" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="e.g., Emergency Fund, Vacation" />
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Type</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {GOAL_TYPES.map((t) => (
                <button key={t.value} onClick={() => setForm({ ...form, goalType: t.value })} style={{
                  padding: '5px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                  border: form.goalType === t.value ? `2px solid ${t.color}` : '1px solid #E5E7EB',
                  backgroundColor: form.goalType === t.value ? t.bg : '#fff',
                  color: form.goalType === t.value ? t.color : '#999', fontWeight: form.goalType === t.value ? 600 : 400,
                }}>{t.label}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Target Amount" value={form.targetAmount} onChange={(v) => setForm({ ...form, targetAmount: v })} placeholder="0.00" type="number" />
            <FormField label="Already Saved" value={form.currentAmount} onChange={(v) => setForm({ ...form, currentAmount: v })} placeholder="0.00" type="number" />
          </div>
          <FormField label="Target Date" value={form.targetDate} onChange={(v) => setForm({ ...form, targetDate: v })} type="date" />
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Emoji</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {GOAL_EMOJIS.map((e) => (
                <button key={e} onClick={() => setForm({ ...form, emoji: e })} style={{ width: 40, height: 40, borderRadius: 10, fontSize: 22, cursor: 'pointer', border: form.emoji === e ? '2px solid #4882F7' : '1px solid #E5E7EB', backgroundColor: form.emoji === e ? '#EFF6FF' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{e}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleAdd} style={{ flex: 1, padding: 12, backgroundColor: '#222', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>Add Goal</button>
            <button onClick={() => { setShowAddForm(false); setForm({ title: '', targetAmount: '', currentAmount: '', emoji: '🎯', targetDate: '', goalType: 'savings' }); }} style={{ padding: '12px 20px', backgroundColor: theme.backgroundColor, color: theme.tertiaryText, border: `1px solid ${theme.borderColor}`, borderRadius: 12, fontSize: 16, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ padding: '0 16px' }}>
          <button onClick={() => setShowAddForm(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: 14, border: `2px dashed ${theme.borderColor}`, borderRadius: 16, backgroundColor: 'transparent', cursor: 'pointer', color: theme.tertiaryText, fontSize: 15, fontWeight: 500 }}>+ Add Goal</button>
        </div>
      )}
    </div>
  );
};

const FormField = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={labelStyle}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} step={type === 'number' ? '0.01' : undefined} style={inputStyle} />
  </div>
);

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#999', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 };
const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid #999', borderRadius: 10, fontSize: 16, outline: 'none', boxSizing: 'border-box', color: 'inherit', backgroundColor: 'transparent' };

export default SavingsPage;
